import { RadialBarChart, RadialBar, Tooltip, ResponsiveContainer, TooltipProps } from 'recharts'
import { ChartSkeleton } from './ChartSkeleton'
import { ChartError } from './ChartError'
import { EmptyChart } from './EmptyChart'
import { usePacketsByManager } from '../hooks/usePacketsByManager'
import { useState, useEffect } from 'react'

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-6))',
  'hsl(var(--chart-7))',
  'hsl(var(--chart-8))',
  'hsl(var(--chart-9))',
  'hsl(var(--chart-10))',
  'hsl(var(--chart-11))',
]

// Custom tooltip for radial chart
function ManagerRadialTooltip({ active, payload }: TooltipProps<number, string>) {
  if (active && payload && payload.length) {
    const d = payload[0]?.payload
    if (d && d.manager && d.count) {
      return (
        <div className="rounded-lg border bg-background p-2 shadow-md">
          <div className="flex items-center gap-2">
            <div
              className="h-2 w-2 rounded-sm"
              style={{ backgroundColor: d.fill }}
            />
            <span className="text-xs font-semibold">{d.manager}</span>
            <span className="text-xs font-semibold ml-1">{d.count.toLocaleString()}</span>
          </div>
        </div>
      )
    }
  }
  return null
}

// Responsive hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return isMobile
}

export function PacketsByManagerChart({ className }: { className?: string }) {
  const { data, isLoading, error } = usePacketsByManager(undefined, 10)
  const isMobile = useIsMobile()

  if (isLoading) return <ChartSkeleton className={className} />
  if (error) return <ChartError className={className} />
  if (!data?.length) return <EmptyChart message="No packets by manager data available" className={className} />

  const total = data.reduce((sum, d) => sum + d.count, 0)

  // Map data: if manager is null, use first part of managerEmail
  let chartData = data.map((entry, idx) => ({
    manager: entry.manager ?? (entry.managerEmail ? entry.managerEmail.split('@')[0] : 'Unknown'),
    count: entry.count,
    fill: COLORS[idx % COLORS.length],
  }))
  // Sort descending so largest arc is first
  chartData = chartData.sort((a, b) => b.count - a.count)

  // Calculate dimensions based on number of rings and screen size
  const numRings = chartData.length
  const minRingWidth = isMobile ? 8 : 12 // Smaller rings on mobile
  const innerRadius = isMobile ? 50 : 90 // Smaller inner radius on mobile
  const outerRadius = innerRadius + (numRings * minRingWidth)
  const containerHeight = Math.max(isMobile ? 280 : 350, outerRadius * 2 + 40)

  return (
    <div className={className}>
      <div className="mb-4">
        <h3 className="text-xl font-semibold">Packets by Manager</h3>
        <p className="text-base text-muted-foreground">Current year packets by top 10 managers</p>
      </div>
      <div className="relative overflow-hidden" style={{ height: `${containerHeight}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            data={chartData}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={90}
            endAngle={-270}
          >
            <RadialBar
              dataKey="count"
              background
              isAnimationActive={false}
              label={false}
            />
            <Tooltip content={ManagerRadialTooltip} cursor={false} />
          </RadialBarChart>
        </ResponsiveContainer>
        {/* Center label styled like PacketsByDecisionChart */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${containerHeight} ${containerHeight}`}>
          <text x={containerHeight/2} y={containerHeight/2} textAnchor="middle" dominantBaseline="middle">
            <tspan x={containerHeight/2} y={containerHeight/2} className={`fill-foreground font-bold ${isMobile ? 'text-2xl' : 'text-4xl'}`}>
              {total.toLocaleString()}
            </tspan>
            <tspan x={containerHeight/2} y={containerHeight/2 + (isMobile ? 16 : 24)} className="fill-muted-foreground text-xs" style={{ textTransform: 'uppercase' }}>
              PACKETS
            </tspan>
          </text>
        </svg>
      </div>
      
      {/* Mobile Legend - Show when rings are too thin to interact with */}
      {isMobile && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Managers</h4>
          <div className="grid grid-cols-1 gap-2 text-sm">
            {chartData.map((entry) => (
              <div key={entry.manager} className="flex items-center justify-between p-2 rounded bg-muted/30">
                <div className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: entry.fill }}
                  />
                  <span className="text-foreground truncate">{entry.manager}</span>
                </div>
                <span className="font-medium text-foreground ml-2 flex-shrink-0">
                  {entry.count.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
} 